import { Injectable, signal } from '@angular/core';

export type ToastKind = 'success' | 'info' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  run: () => void;
}

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
  /** null = persistente: solo se cierra manualmente. */
  duration: number | null;
  dedupeKey?: string;
}

export interface ToastOptions {
  /** Milisegundos hasta el auto-cierre; null desactiva el auto-cierre. */
  duration?: number | null;
  /**
   * Si un duplicado ya está visible, el toast nuevo se descarta entero (solo se
   * reinicia el timer del existente): esta `action` NO reemplaza a la anterior.
   * Para acciones con closures distintos usar `dedupeKey` diferenciados.
   */
  action?: ToastAction;
  /** Clave de deduplicación explícita; sin ella se deduplica por kind + message. */
  dedupeKey?: string;
}

// Duraciones por defecto según severidad: los errores persisten hasta cierre manual
// porque el usuario tiene que poder leerlos con calma (y suelen requerir acción).
const DEFAULT_DURATIONS: Record<ToastKind, number | null> = {
  success: 5000,
  info: 5000,
  warning: 7000,
  error: null,
};

// Máximo de toasts visibles a la vez; los que sobran esperan en cola FIFO
// y se promueven a medida que los visibles se cierran.
const MAX_VISIBLE = 4;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly visible = signal<readonly Toast[]>([]);
  private readonly queued: Toast[] = [];
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  private nextId = 1;

  /** Toasts visibles, en orden de llegada (el más viejo primero). */
  readonly toasts = this.visible.asReadonly();

  success(message: string, opts?: ToastOptions): number {
    return this.show('success', message, opts);
  }

  info(message: string, opts?: ToastOptions): number {
    return this.show('info', message, opts);
  }

  warning(message: string, opts?: ToastOptions): number {
    return this.show('warning', message, opts);
  }

  error(message: string, opts?: ToastOptions): number {
    return this.show('error', message, opts);
  }

  show(kind: ToastKind, message: string, opts?: ToastOptions): number {
    const duplicate = this.findDuplicate(kind, message, opts?.dedupeKey);
    if (duplicate) {
      // No apilamos duplicados: se reinicia el timer del que ya está visible.
      this.restartTimer(duplicate);
      return duplicate.id;
    }

    const toast: Toast = {
      id: this.nextId++,
      kind,
      message,
      duration: opts?.duration === undefined ? DEFAULT_DURATIONS[kind] : opts.duration,
      ...(opts?.action !== undefined && { action: opts.action }),
      ...(opts?.dedupeKey !== undefined && { dedupeKey: opts.dedupeKey }),
    };

    if (this.visible().length < MAX_VISIBLE) {
      this.display(toast);
    } else {
      this.queued.push(toast);
    }
    return toast.id;
  }

  dismiss(id: number): void {
    this.clearTimer(id);

    const queuedIndex = this.queued.findIndex((t) => t.id === id);
    if (queuedIndex >= 0) {
      this.queued.splice(queuedIndex, 1);
      return;
    }

    if (!this.visible().some((t) => t.id === id)) {
      return;
    }
    this.visible.update((list) => list.filter((t) => t.id !== id));

    const next = this.queued.shift();
    if (next) {
      this.display(next);
    }
  }

  clear(): void {
    for (const timer of this.timers.values()) {
      clearTimeout(timer);
    }
    this.timers.clear();
    this.queued.length = 0;
    this.visible.set([]);
  }

  private findDuplicate(kind: ToastKind, message: string, dedupeKey?: string): Toast | undefined {
    const matches = (t: Toast) =>
      dedupeKey !== undefined
        ? t.dedupeKey === dedupeKey
        : t.dedupeKey === undefined && t.kind === kind && t.message === message;
    return this.visible().find(matches) ?? this.queued.find(matches);
  }

  private display(toast: Toast): void {
    this.visible.update((list) => [...list, toast]);
    this.startTimer(toast);
  }

  private startTimer(toast: Toast): void {
    if (toast.duration === null) {
      return;
    }
    this.timers.set(
      toast.id,
      setTimeout(() => this.dismiss(toast.id), toast.duration),
    );
  }

  private restartTimer(toast: Toast): void {
    this.clearTimer(toast.id);
    // Un duplicado encolado (todavía no visible) no lleva timer: arranca al mostrarse.
    if (this.visible().some((t) => t.id === toast.id)) {
      this.startTimer(toast);
    }
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }
}
